// Components==============
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/UI/Button/Button';
import EndpointDescription from 'components/UI/EndpointDescription/EndpointDescription';
import ProjectEvidenceButton from 'components/UI/ProjectEvidenceButton/ProjectEvidenceButton';
import Token from 'components/UI/Token/Token';
import TrainingCard from 'components/UI/TrainingCard/TrainingCard';
import { AnimatePresence, motion } from 'framer-motion';
import { usePermission } from 'hooks/usePermission';
import { ProcessGroups, useProcessGroups } from 'hooks/useProcessGroups';
import { useRedux } from 'hooks/useRedux';
import { useToken } from 'hooks/useToken';
import { useTranslate } from 'hooks/useTranslate';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { ReflectionSaveState } from 'typings';
import { interactiveScale } from 'utilities/FramerAnimations';
import { GlobalReflectionContext } from 'utilities/GlobalContext/GlobalReflectionContext';
import { getInitialReflectionSaveState } from 'utilities/getInitialReflectionSaveState';
import { groupSparksBySkill } from 'utilities/groupSparksBySkill';
import { ReflectContext } from '../../Reflect';
import './ReflectionItem.module.scss';
import ActivityReflection from './components/ActivityReflection/ActivityReflection';
import EndPointReflection from './components/EndPointReflection/EndPointReflection';
import PeerEndPointReflection from './components/PeerEndPointReflection/PeerEndPointReflection';
import PeerReflection from './components/PeerReflection/PeerReflection';
// =========================

type ReflectionItemContextType = {
  saveState: ReflectionSaveState;
  setSaveState: React.Dispatch<React.SetStateAction<ReflectionSaveState>>;
  reflection?: ReflectionEntity;
  reflectingOn?: ReflectionEntity;
  save: (state: ReflectionSaveState) => void;
  activity: ProcessProcessEntity;
  processGroups: ProcessGroups;
  setProcessGroups: React.Dispatch<React.SetStateAction<ProcessGroups>>;
  resetProcessGroups: () => void;
  userSelectedSparks?: SparkEntity[];
  setUserSelectedSparks?: React.Dispatch<React.SetStateAction<SparkEntity[]>>;
  handleClick?: (id: number) => void;
  allProjectSparks?: (SparkEntity | undefined)[];
  groupedSparks?: GroupedSparks;
  skillObj?: { [key: string]: SkillEntity } | undefined;
};

export const ReflectionItemContext = createContext(
  {} as ReflectionItemContextType
);

export default function ReflectionItem({
  activity,
  checkpointDone,
}: {
  activity: ProcessProcessEntity;
  checkpointDone?: boolean;
}) {
  const translate = useTranslate();

  const {
    handleSave,
    peerId,
    wrapperRef,
    currentStudent,
    updateCounter,
    allReflectionsAreCompleted,
    scrollToCard,
    toggled,
  } = useContext(ReflectContext);

  const { selectedReflection, setSelectedReflection } = useContext(
    GlobalReflectionContext
  );

  const ref = useRef<HTMLDivElement>(null);

  const isCheckpoint = activity.process_id === 3;
  const isEndPoint = activity.process_type === 'App\\Phase';

  const token = useToken(activity);

  const project = useRedux(
    (state) => state.project?.entities.byId?.[activity.project_id]
  );

  const userId = useRedux((state) => state.authentication?.user?.id);

  const isMember =
    project?.members.find((member) => member.user_id === userId) !== undefined;
  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);

  const reflection = useRedux((state) =>
    peerId
      ? state.reflection?.entities.byId[parseInt(peerId)]
      : state.reflection?.entities.allIds
          .map((id) => state.reflection?.entities.byId[id])
          .find((r) =>
            isTeacher
              ? r?.project_process_id === activity.id &&
                r.created_by === userId &&
                r.for_user?.id === currentStudent?.userId
              : r?.project_process_id === activity.id && r.created_by === userId
          )
  );

  const reflectingOn = useRedux((state) =>
    state.reflection?.entities.allIds
      .map((id) => state.reflection?.entities.byId[id])
      .find(
        (r) =>
          r?.project_process_id === activity.id &&
          (peerId
            ? // peer reflection
              r?.created_by === reflection?.for_user?.id
            : // teacher reflection (possible no reflection available)
              !r?.for_user && currentStudent?.userId === r?.created_by)
      )
  );

  const [saveState, setSaveState] = useState<ReflectionSaveState>(() =>
    getInitialReflectionSaveState(activity.id, reflection)
  );

  const [isOpen, setIsOpen] = useState(
    selectedReflection === reflection?.id || !!peerId
  );

  useEffect(() => {
    if (toggled) setIsOpen(true);
    if (!toggled) setIsOpen(false);
  }, [toggled]);

  useEffect(() => {
    if (selectedReflection === (reflection?.id || reflectingOn?.id)) {
      setIsOpen(true);
      scrollToCard(ref);
      setSelectedReflection(0);
    }
  }, [selectedReflection === (reflection?.id || reflectingOn?.id)]);

  useEffect(() => {
    // if project_process_id was updated but reflection wasn't yet
    if (reflection?.id && !saveState.id)
      setSaveState(getInitialReflectionSaveState(activity.id, reflection));
  }, [reflection?.id]);

  useEffect(() => {
    if (!updateCounter) return;
    setSaveState(getInitialReflectionSaveState(activity.id, reflection));
  }, [updateCounter]);

  const save = (state: ReflectionSaveState) => handleSave(state, setSaveState);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (wrapperRef.current && isOpen && isEndPoint)
        wrapperRef.current.scrollLeft += 10000;
    }, 100);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const hasContent = project?.process ? project?.process.length > 0 : false;

  const [saveKey, setSaveKey] = useState(0);

  useEffect(() => setSaveKey((prev) => prev + 1), [hasContent]);

  const {
    processGroups,
    setProcessGroups,
    resetProcessGroups,
  } = useProcessGroups(project, saveKey);

  const isFinishedTeacher =
    !!(reflectingOn?.finished && reflectingOn.contributed) ||
    !!(reflectingOn?.contributed !== undefined && !reflectingOn.contributed);

  const isFinishedStudent =
    !!(reflection?.finished && reflection.contributed) ||
    !!(reflection?.contributed !== undefined && !reflection.contributed);

  const [allSparks, setAllSparks] = useState<SparkEntity[]>([]);

  const saveStateSparks = useRedux((state) =>
    saveState.sparks.map((spark) => state.spark?.entities.byId[spark])
  );

  const sparks = saveStateSparks.length
    ? saveStateSparks
    : reflection?.sparks.length
    ? reflection.sparks
    : [];

  useEffect(() => {
    const mergedSparks = [
      ...(sparks || []),
      ...(activity?.description?.spark_goals || []),
    ];

    // Filter out duplicate sparks based on their id
    const uniqueSparks = mergedSparks.reduce<SparkEntity[]>((acc, spark) => {
      console.log(spark);
      if (!acc.some((item) => item.id === spark.id)) {
        acc.push(spark);
      }
      return acc;
    }, []);

    setAllSparks(uniqueSparks);
  }, []);

  const [userSelectedSparks, setUserSelectedSparks] = useState<SparkEntity[]>(
    allSparks || []
  );

  const process = processGroups?.find((g) => g.activities[0]?.id === 7);

  const stateChangeHelper = (newVal: EndPointType) =>
    setProcessGroups((prev) =>
      prev.map((group) => {
        if (
          group.id === process?.id &&
          group.process_type === process.process_type
        ) {
          return {
            ...group,
            description: {
              ...(group.description as EndPointType),
              ...newVal,
            },
          };
        }
        return group;
      })
    );

  function updateDescriptionWithSparkGoals(sparksGoals: SparkEntity[]) {
    stateChangeHelper({ spark_goals: sparksGoals });
  }

  function handleClick(id: number) {
    setUserSelectedSparks((prevSparks) => {
      const updatedSparks = prevSparks.map((spark) => {
        if (spark.id === id) {
          return { ...spark, done: !spark.done };
        }
        return spark;
      });
      updateDescriptionWithSparkGoals(
        (updatedSparks as unknown) as SparkEntity[]
      );
      return updatedSparks as SparkEntity[];
    });
  }

  const skillObj = useRedux((state) => state.skill?.entities.byId);

  const { projectId } = useParams() as {
    projectId: string;
  };

  const reflections = useRedux((state) => {
    const allReflections = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    if (isTeacher) {
      return allReflections?.filter(
        (r) =>
          r?.created_by === currentStudent?.userId &&
          r?.project_process?.project_id === parseInt(projectId)
      );
    }

    return allReflections?.filter(
      (r) =>
        r?.project_process?.project_id === parseInt(projectId) &&
        r?.created_by === userId
    );
  });

  // get project sparks linked to end point
  const projectSparks = project?.sparks || sparks;

  // get other sparks linked to activities
  const reflectionSparks = reflections?.map((r) => r?.sparks).flat();

  // accumulate all sparks
  const allProjectSparks =
    // check if reflectionSparks and projectSparks are not undefined
    reflectionSparks?.length && projectSparks?.length
      ? // if so, check if there are any duplicates and remove them
        [...reflectionSparks, ...projectSparks].reduce((acc, current) => {
          const x = acc.find((item) => item.id === current?.id!);
          if (!x) {
            return acc.concat([current!]);
          }
          return acc;
        }, [] as SparkEntity[])
      : reflectionSparks?.length
      ? reflectionSparks
      : projectSparks;

  const groupedSparks = groupSparksBySkill(
    projectSparks as SparkEntity[],
    skillObj,
    reflections as ReflectionEntity[]
  );

  return (
    <ReflectionItemContext.Provider
      value={{
        saveState,
        setSaveState,
        reflection,
        save,
        activity,
        reflectingOn,
        processGroups,
        setProcessGroups,
        resetProcessGroups,
        userSelectedSparks,
        setUserSelectedSparks,
        handleClick,
        allProjectSparks,
        groupedSparks,
        skillObj,
      }}
    >
      <div styleName="container">
        <div ref={ref} styleName="scroll-to" />
        <motion.div
          styleName={`wrapper ${
            isEndPoint ? 'is-end-point' : isOpen ? 'open' : ''
          }`}
          {...interactiveScale}
          onClick={(e) => {
            e.stopPropagation();
            if (isCheckpoint) return;
            setIsOpen((prev) => !prev);
          }}
        >
          {!peerId && (
            <>
              {(
                isEndPoint
                  ? !!allReflectionsAreCompleted
                  : (isTeacher ? isFinishedTeacher : isFinishedStudent) ||
                    checkpointDone
              ) ? (
                <div styleName="finish-circle finished">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              ) : (
                <div styleName="finish-circle" />
              )}
            </>
          )}
          {token && 'thumbnail' in token ? (
            <div styleName={isEndPoint ? 'end-point' : ' '}>
              <Token token={token} withMargin />
            </div>
          ) : (
            <div styleName="training">
              <TrainingCard training={token} withModal small />
            </div>
          )}
          {typeof activity?.description === 'string' && activity.description && (
            <div styleName="description">
              <strong>{translate('general.terms.explanation')}</strong>
              <p>{activity?.description}</p>
            </div>
          )}
          {isEndPoint && activity.description && project ? (
            <EndpointDescription
              description={activity.description as EndPointType}
              project={project}
              readOnly
            />
          ) : isCheckpoint ? (
            <>
              <div styleName="bar" />
              <div styleName="button">
                <Button
                  to={
                    checkpointDone
                      ? `/werkplaats/project-details/${activity.project_id}`
                      : undefined
                  }
                  inactive={!checkpointDone}
                  color={checkpointDone ? 'green' : 'double-grey'}
                >
                  {translate(
                    `reflection.${
                      checkpointDone ? 'terms.reflection-done' : 'not-reflected'
                    }`
                  )}
                </Button>
              </div>
            </>
          ) : (
            !!activity && (
              <ProjectEvidenceButton
                process={activity}
                rootModal
                disableComments={!isMember && !isTeacher}
              />
            )
          )}
        </motion.div>
        <AnimatePresence>
          {isOpen &&
            !isCheckpoint &&
            (isEndPoint ? (
              isTeacher ? (
                <PeerEndPointReflection />
              ) : (
                <EndPointReflection completed={allReflectionsAreCompleted} />
              )
            ) : !!peerId || isTeacher ? (
              <PeerReflection />
            ) : (
              <ActivityReflection />
            ))}
        </AnimatePresence>
      </div>
    </ReflectionItemContext.Provider>
  );
}
